import React, { useCallback, useEffect, useState } from 'react';
import { Col } from '../col/col.component';
import { Write } from '../write/write.component';
import './passwordStrengthValidator.component.scss';

const SPECIAL_CHARACTERS = '!@#$%^&*.';
const SPECIAL_CHARACTERS_REGEX = new RegExp(`[${SPECIAL_CHARACTERS}]`);

interface PasswordCriteria {
  minCharacters: boolean;
  uppercaseLetter: boolean;
  lowercaseLetter: boolean;
  digit: boolean;
  specialCharacter: boolean;
}

interface PasswordStrength {
  entropy: number;
  level: 'very_weak' | 'weak' | 'medium' | 'strong' | 'very_strong' | null;
  percentage: number;
}

export interface PasswordValidationResult {
  criteria: PasswordCriteria;
  strength: PasswordStrength;
  isValid: boolean;
}

export declare namespace PasswordStrengthValidatorType {
  type Props = {
    className?: string;
    data: {
      password: string | null | undefined;
      touched?: boolean;
      minLength?: number;
    };
    config?: {
      showStrengthBar?: boolean;
      showCriteria?: boolean;
    };
    handleEvent?: {
      change?: (result: PasswordValidationResult) => void;
    };
    // Fonction pour obtenir les traductions
    msgFn: (key: string) => string;
  };
}

export const PasswordStrengthValidator = ({
  className = '',
  data: { password, touched = false, minLength = 12 },
  config: { showStrengthBar = true, showCriteria = true } = {},
  handleEvent: { change } = {},
  msgFn,
}: PasswordStrengthValidatorType.Props) => {
  const [passwordCriteria, setPasswordCriteria] = useState<PasswordCriteria>({
    minCharacters: false,
    uppercaseLetter: false,
    lowercaseLetter: false,
    digit: false,
    specialCharacter: false,
  });

  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    entropy: 0,
    level: null,
    percentage: 0,
  });

  const validatePasswordCriteria = useCallback(
    (pwd: string | null | undefined): PasswordCriteria => {
      if (!pwd) {
        return {
          minCharacters: false,
          uppercaseLetter: false,
          lowercaseLetter: false,
          digit: false,
          specialCharacter: false,
        };
      }

      return {
        minCharacters: new RegExp(`^(?=.{${minLength},})`).test(pwd),
        uppercaseLetter: /^(?=.*[A-Z])/.test(pwd),
        lowercaseLetter: /^(?=.*[a-z])/.test(pwd),
        digit: /^(?=.*\d)/.test(pwd),
        specialCharacter: SPECIAL_CHARACTERS_REGEX.test(pwd),
      };
    },
    [minLength],
  );

  // Calcul de la force du mot de passe selon les critères ANSSI
  // Basé sur l'entropie : H = L × log2(N)
  // où L = longueur, N = taille de l'alphabet
  const calculatePasswordStrength = useCallback(
    (pwd: string | null | undefined): PasswordStrength => {
      if (!pwd || pwd.length === 0) {
        return {
          entropy: 0,
          level: null,
          percentage: 0,
        };
      }

      let alphabetSize = 0;

      // Calcul de la taille de l'alphabet selon les caractères utilisés
      if (/[a-z]/.test(pwd)) alphabetSize += 26; // Minuscules
      if (/[A-Z]/.test(pwd)) alphabetSize += 26; // Majuscules
      if (/\d/.test(pwd)) alphabetSize += 10; // Chiffres
      if (SPECIAL_CHARACTERS_REGEX.test(pwd)) alphabetSize += 32; // Caractères spéciaux

      // Calcul de l'entropie : H = L × log2(N)
      const entropy = pwd.length * Math.log2(alphabetSize);

      // Niveaux selon les recommandations ANSSI
      let level: 'very_weak' | 'weak' | 'medium' | 'strong' | 'very_strong';
      let percentage: number;

      if (entropy < 40) {
        level = 'very_weak';
        percentage = Math.min((entropy / 40) * 25, 25);
      } else if (entropy < 60) {
        level = 'weak';
        percentage = 25 + ((entropy - 40) / 20) * 25;
      } else if (entropy < 80) {
        level = 'medium';
        percentage = 50 + ((entropy - 60) / 20) * 25;
      } else if (entropy < 100) {
        level = 'strong';
        percentage = 75 + ((entropy - 80) / 20) * 15;
      } else {
        level = 'very_strong';
        percentage = Math.min(90 + ((entropy - 100) / 50) * 10, 100);
      }

      return {
        entropy: Math.round(entropy * 10) / 10,
        level,
        percentage: Math.round(percentage),
      };
    },
    [],
  );

  useEffect(() => {
    const criteria = validatePasswordCriteria(password);
    const strength = calculatePasswordStrength(password);

    setPasswordCriteria(criteria);
    setPasswordStrength(strength);

    // Vérifier si tous les critères sont remplis
    const isValid = Object.values(criteria).every((criterion) => criterion);

    // Appeler le callback si fourni
    change?.({ criteria, strength, isValid });
  }, [password, validatePasswordCriteria, calculatePasswordStrength, change]);

  const getStrengthColor = (
    level: PasswordStrength['level'],
  ): string => {
    switch (level) {
      case 'very_weak':
        return '#EF4444';
      case 'weak':
        return '#F97316';
      case 'medium':
        return '#F59E0B';
      case 'strong':
        return '#10B981';
      case 'very_strong':
        return '#059669';
      default:
        return '#E5E7EB';
    }
  };

  const getStrengthLabel = (level: PasswordStrength['level']): string => {
    switch (level) {
      case 'very_weak':
        return msgFn('veryWeak');
      case 'weak':
        return msgFn('weak');
      case 'medium':
        return msgFn('medium');
      case 'strong':
        return msgFn('strong');
      case 'very_strong':
        return msgFn('veryStrong');
      default:
        return '';
    }
  };

  const getCriteriaColor = (isValid: boolean): string => {
    if (!touched) return '#6B7280';
    return isValid ? '#10B981' : '#EF4444';
  };

  return (
    <div className={`passwordStrengthValidator ${className}`}>
      {showStrengthBar && (
        <div className="passwordStrengthValidator__strength">
          <div className="passwordStrengthValidator__strength__header">
            <span className="passwordStrengthValidator__strength__header__label">
              {msgFn('passwordStrength')}
            </span>
            {passwordStrength.level && (
              <span
                className="passwordStrengthValidator__strength__header__level"
                style={{ color: getStrengthColor(passwordStrength.level) }}
              >
                {getStrengthLabel(passwordStrength.level)}
              </span>
            )}
          </div>

          <div className="passwordStrengthValidator__strength__bar">
            {[1, 2, 3, 4, 5].map((segment) => {
              const isActive =
                passwordStrength.level &&
                ((passwordStrength.level === 'very_weak' && segment <= 1) ||
                  (passwordStrength.level === 'weak' && segment <= 2) ||
                  (passwordStrength.level === 'medium' && segment <= 3) ||
                  (passwordStrength.level === 'strong' && segment <= 4) ||
                  (passwordStrength.level === 'very_strong' && segment <= 5));

              const segmentColor = isActive
                ? getStrengthColor(passwordStrength.level)
                : '#E5E7EB';

              return (
                <div
                  key={segment}
                  className="passwordStrengthValidator__strength__bar__segment"
                  style={{ backgroundColor: segmentColor }}
                />
              );
            })}
          </div>
        </div>
      )}

      {showCriteria && (
        <Col config={{ gap: 4 }}>
          <div
            style={{
              color: getCriteriaColor(passwordCriteria.minCharacters),
              fontSize: '12px',
              lineHeight: '18px',
            }}
          >
            {msgFn('MIN_CHARACTERS').replace('[MIN]', minLength.toString())}
          </div>
          <div
            style={{
              color: getCriteriaColor(passwordCriteria.uppercaseLetter),
              fontSize: '12px',
              lineHeight: '18px',
            }}
          >
            {msgFn('UPPERCASE_LETTER')}
          </div>
          <div
            style={{
              color: getCriteriaColor(passwordCriteria.lowercaseLetter),
              fontSize: '12px',
              lineHeight: '18px',
            }}
          >
            {msgFn('LOWERCASE_LETTER')}
          </div>
          <div
            style={{
              color: getCriteriaColor(passwordCriteria.digit),
              fontSize: '12px',
              lineHeight: '18px',
            }}
          >
            {msgFn('DIGIT')}
          </div>
          <div
            style={{
              color: getCriteriaColor(passwordCriteria.specialCharacter),
              fontSize: '12px',
              lineHeight: '18px',
            }}
          >
            {msgFn('SPECIAL_CHARACTER').replace('[CHARACTERS]', SPECIAL_CHARACTERS)}
          </div>
        </Col>
      )}
    </div>
  );
};
